import React, { useState } from 'react';
import axios from 'axios';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import AuthLayout from './landing/AuthLayout';

/*
 * Main-app forgot-password page (v2 auth chrome). Wired to the existing
 * POST /auth/forgot-password endpoint. `resetUrlPath` must be on the backend
 * allow-list (ALLOWED_RESET_URL_PATHS); '/reset-password' is the default there.
 * The backend returns the same response whether or not the email exists, so we
 * always show the success view on 200 (no account enumeration).
 */
const ForgotPassword = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const backendUrl = window.__BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!email) { setError('missingFields'); return; }
    if (!backendUrl) { setError('backendUnavailable'); return; }

    try {
      setSubmitting(true);
      await axios.post(
        `${backendUrl}/auth/forgot-password`,
        { email, resetUrlPath: '/reset-password' },
        { withCredentials: true }
      );
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.status === 429 ? 'rateLimited' : 'generic');
    } finally {
      setSubmitting(false);
    }
  };

  const errorText = error
    ? t(`terminal.forgotPassword.errors.${error}`, { defaultValue: t('terminal.forgotPassword.errors.generic', 'Something went wrong. Please try again.') })
    : null;

  const backToSignIn = (
    <p className="auth-switch">
      <RouterLink to="/login">{t('terminal.forgotPassword.backToSignIn', 'Back to sign in')}</RouterLink>
    </p>
  );

  return (
    <AuthLayout>
      <SEO title="Reset password" path="/forgot-password" noindex />

      {submitted ? (
        <div className="auth-center">
          <div className="auth-success-icon" aria-hidden="true">✓</div>
          <h1 className="auth-title">{t('terminal.forgotPassword.successTitle', 'Check your email')}</h1>
          <p className="auth-sub">
            {t('terminal.forgotPassword.successMessage', "If an account exists for that email, we've sent a link to reset your password. The link expires in 1 hour.")}
          </p>
          {backToSignIn}
        </div>
      ) : (
        <>
          <h1 className="auth-title">{t('terminal.forgotPassword.title', 'Reset password')}</h1>
          <p className="auth-sub">{t('terminal.forgotPassword.description', "Enter your email and we'll send you a link to reset your password.")}</p>

          {errorText && <div className="auth-alert error" role="alert">{errorText}</div>}

          <form className="auth-form" onSubmit={handleSubmit} noValidate>
            <div className="field">
              <label htmlFor="fp-email">{t('terminal.forgotPassword.emailLabel', 'Email')}</label>
              <input
                id="fp-email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
                required
                autoComplete="email"
                autoFocus
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={submitting} aria-busy={submitting}>
              {submitting ? <span className="spinner" /> : t('terminal.forgotPassword.submitButton', 'Send reset link')}
            </button>
          </form>

          {backToSignIn}
        </>
      )}
    </AuthLayout>
  );
};

export default ForgotPassword;
